/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faCheck, faEye, faTimes } from "@fortawesome/pro-regular-svg-icons";
import React, { useEffect, useState } from "react";
import { Badge, Input } from "reactstrap";

import { Button } from "../../../Helper/Button";
import { ConfirmationModal } from "../../../Helper/Modal";
import { useOfflineBarHeight } from "../../Layout/offlineBarHeight";
import useRipeningRoom from "../../Ripening/hooks/useRipeningRoom";
import SignalDetailModal from "../SignalDetailModal";
import { isClosed } from "../signalFiltering";
import { useUpdateSignal } from "../signalHooks";
import { useActiveSignal } from "../useActiveSignal";
import { CATEGORY_COLOR, CATEGORY_LABEL, getSignalCategory, Signal } from "../signalTypes";
import * as styles from "./activeSignalBar.styles";

/** Who closed the signal, for the conflict notice (D2).
 *
 *  The audit timeline comes back oldest-first and the engine always writes a
 *  `created` event with actor "system" first, so `events[0].actor` is never the
 *  closer — it is the string "system". Read the NEWEST resolved/rejected entry.
 */
const closingActor = (signal: Signal): string | undefined => [...(signal.events || [])]
    .reverse()
    .find((event) => event.event === "resolved" || event.event === "rejected")
    ?.actor;

/**
 * The floating bar naming the signal the operator is working (D6). It reads the
 * active signal from the URL, so a reload keeps it and the back button clears
 * it. Mounted outside SidebarPageLayout's content flow so its `position: fixed`
 * resolves against the viewport.
 */
export const ActiveSignalBar: React.FC = () => {
    const { currentLocation } = useRipeningRoom();
    const { activeSignal, clearActiveSignal } = useActiveSignal(currentLocation?.location_id);
    const updateSignal = useUpdateSignal();
    const offlineBarHeight = useOfflineBarHeight();

    // D8: Resolve/Reject prompt for an optional note BEFORE writing. A note
    // attached at the moment of decision is the audit trail's whole value.
    const [pendingStatus, setPendingStatus] = useState<"resolved" | "rejected" | null>(null);
    const [note, setNote] = useState("");
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    // This component is mounted for the whole life of the room page — it is
    // rendered unconditionally under the feature flag and merely returns null
    // when no signal is active — so switching signals does NOT tear its dialog
    // state down. Without this reset, activating the next signal inherits the
    // previous one's open Detail modal (which hides the bar it is meant to
    // reveal — design invariant 2) and an armed Confirm still pointing at a
    // status the operator never chose for THIS signal.
    const activeSignalId = activeSignal?.id;
    useEffect(() => {
        setPendingStatus(null);
        setNote("");
        setIsDetailOpen(false);
    }, [activeSignalId]);

    if (!activeSignal) return null;

    const category = getSignalCategory(activeSignal);
    const closed = isClosed(activeSignal);
    const closer = closingActor(activeSignal);
    // Only ever rendered for someone ELSE's close: our own resolve clears the
    // param on mutation success, so the bar is already gone. No bookkeeping.
    const conflictNotice = `Already ${activeSignal.status}${closer ? ` by ${closer}` : ""}`;

    const openPrompt = (status: "resolved" | "rejected") => {
        setNote("");
        setPendingStatus(status);
    };

    // The same useUpdateSignal mutation the detail modal, the room tab and the
    // bulk bar use, so the audit event, the note and the cache invalidation are
    // identical to every other path.
    const writeStatus = async (status: "resolved" | "rejected", noteText?: string) => {
        // Second layer under the prompt's own `!closed` gate, and deliberately
        // one NO unit test can kill: a click dispatched before React has
        // processed the poll's re-render carries the same stale `activeSignal`
        // this reads, so the gate above is what actually closes the hole. It is
        // kept as the tripwire for the two ways the hole reopens — someone
        // ungating the prompt, or a third caller of writeStatus (the detail
        // modal is already a second). What it guards against is real: writing a
        // status onto an already-closed signal overwrites the closer's decision
        // AND appends a second closing event, since `rejected` -> `resolved` is
        // a genuine transition and signal.py:347 audits every genuine one.
        if (isClosed(activeSignal)) return;
        await updateSignal.mutateAsync({ signalId: activeSignal.id, status, note: noteText });
        clearActiveSignal();
    };

    // The bar floats outside the page's content flow, so it is a named landmark
    // — which also gives the e2e suite a role/name locator instead of a test id.
    return (
        <div role="region" aria-label="Active signal" css={styles.activeSignalBarStyles(offlineBarHeight)}>
            <div className="rounded shadow-lg p-3 bg-white w-100 d-flex align-items-center gap-3">
                {/* D14: the bar's own exit. It is the FIRST item in the row (before
                    the badge/title) and NOT in the right-hand action group below —
                    that group is flex-shrink-0 and the title truncates against it
                    (invariant 4), so putting the exit there would make it compete
                    with the signal's name for width on a narrow screen. It is
                    present in every state, alongside the conflict state's own
                    labelled Dismiss (kept deliberately — see D14 for why the
                    redundancy is intended). align-self-start keeps it top-left
                    rather than vertically centred once the conflict notice wraps
                    the bar onto two rows (D2). It only clears the URL param —
                    never wired to useUpdateSignal — so it can never write a status
                    the operator did not choose. */}
                <Button
                    color="secondary"
                    outline
                    rounded
                    icon={faTimes}
                    // "Close signal bar", not "Close": by ear, "Close, button"
                    // next to a signal titled "CO2 high" reads as closing the
                    // SIGNAL. This control only stops showing the bar.
                    aria-label="Close signal bar"
                    className="align-self-start"
                    onClick={clearActiveSignal}
                />
                {/* min-width: 0 is what lets either text give way. A flex item's
                    automatic minimum size is its content, so without this a long title
                    pushes this column — and the bar with it — wider than the viewport,
                    and text-truncate never gets a width to truncate against. Needed on
                    the column AND on each text node, since each is a flex item.

                    The conflict notice belongs in HERE, not next to the buttons: the
                    actor is a full email address (signal.py writes `user.email`), so
                    beside a flex-shrink-0 button group it would push the bar off-screen
                    on a phone with nothing able to give way. */}
                {/* Below md the notice gets its OWN line: sharing the row with the
                    title leaves it a ~50px sliver, and wrapping inside that turns the
                    bar into a 200px-tall ribbon of two-character lines. From md up
                    there is room for one row. */}
                <div className="flex-grow-1 d-flex flex-column flex-md-row gap-1 gap-md-2 align-items-md-center" css={css`min-width: 0;`}>
                    <div className="d-flex align-items-center gap-2" css={css`min-width: 0;`}>
                        {category && <Badge className="flex-shrink-0" color={CATEGORY_COLOR[category]}>{CATEGORY_LABEL[category]}</Badge>}
                        {/* The title truncates on ONE line (invariant 4). A title is
                            recognisable from its first words, and one that wrapped
                            would cost a second line on every signal, open or closed. */}
                        <span className="fw-bold text-truncate" css={css`min-width: 0;`}>{activeSignal.title}</span>
                    </div>
                    {/* The notice is the one element allowed to WRAP, and the only one
                        that needs to be. An audit statement is NOT recognisable from
                        its first words, and no truncation setting fits "Already
                        rejected by firstname.lastname@customer-company.com" into the
                        ~110px left at 375px — measured, it reads "Already rejecte…".
                        So the bar grows taller while a conflict is showing and nothing
                        is cut off. text-break because an email address is a single
                        long token with nowhere for normal word wrapping to break. */}
                    {closed && <span className="text-muted small text-break" css={css`min-width: 0;`}>{conflictNotice}</span>}
                </div>

                {/* D14: Helper/Button renders its label in a `d-none d-md-flex` span,
                    and EVERY button in this group uses iconOnlyBreakpoint="md" — so
                    below md an explicit aria-label is the only accessible name these
                    controls have. Each label deliberately BEGINS with the button's own
                    visible word (WCAG 2.5.3 Label in Name), so "click Detail" still
                    matches for voice control.

                    These names are also what the Playwright fixtures locate the bar's
                    buttons by (e2e/fixtures/signalsFlows.ts) — aria-label overrides
                    name-from-content, so changing one of these strings breaks specs
                    15, 16 and 21 unless the fixture regex changes with it. */}
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    {closed ? (
                        <Button color="secondary" outline icon={faTimes} iconOnlyBreakpoint="md" aria-label="Dismiss notice" onClick={clearActiveSignal}>Dismiss</Button>
                    ) : (
                        <>
                            <Button color="success" icon={faCheck} iconOnlyBreakpoint="md" aria-label="Resolve signal" onClick={() => openPrompt("resolved")} isLoading={updateSignal.isPending}>Resolve</Button>
                            <Button color="danger" outline icon={faTimes} iconOnlyBreakpoint="md" aria-label="Reject signal" onClick={() => openPrompt("rejected")} isLoading={updateSignal.isPending}>Reject</Button>
                        </>
                    )}
                    {/* Detail survives into the closed state on purpose: "someone else
                        closed this" is exactly when you want to read what happened, and
                        the modal is read-only for a closed signal. The safety property
                        D2 asks for is that Resolve/Reject are gone, which they are. */}
                    <Button color="secondary" outline icon={faEye} iconOnlyBreakpoint="md" aria-label="Detail for this signal" onClick={() => setIsDetailOpen(true)}>Detail</Button>
                </div>
            </div>

            <ConfirmationModal
                isOpen={!!pendingStatus && !closed}
                header={pendingStatus === "rejected" ? "Reject signal" : "Resolve signal"}
                color={pendingStatus === "rejected" ? "danger" : "success"}
                size="md"
                onConfirm={() => { if (pendingStatus) writeStatus(pendingStatus, note.trim() || undefined); }}
                onClose={() => setPendingStatus(null)}
            >
                <Input
                    type="textarea"
                    rows={2}
                    placeholder="Optional note (attached to resolve / reject)…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </ConfirmationModal>

            {/* The modal is no longer the only way in — it layers over the bar. */}
            <SignalDetailModal
                signal={activeSignal}
                isOpen={isDetailOpen}
                onClose={() => setIsDetailOpen(false)}
                onResolveReject={(_signalId, status, noteText) => writeStatus(status, noteText)}
                isSubmitting={updateSignal.isPending}
            />
        </div>
    );
};

export default ActiveSignalBar;
